import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { 
    DollarSign, 
    Calendar, 
    Eye, 
    RefreshCw, 
    User,
    CreditCard,
    Clock,
    Search,
    ArrowUpDown,
    ArrowLeft,
    Filter
} from 'lucide-react';
import axios from 'axios';
import { Card } from '../../components/ui/card';

const OnlinePayments = () => {
    const [payments, setPayments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [totalAmount, setTotalAmount] = useState(0);
    const [searchTerm, setSearchTerm] = useState('');
    const [showFilters, setShowFilters] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        fetchOnlinePayments();
    }, []);

    const fetchOnlinePayments = async () => {
        try {
            const { data } = await axios.get('/api/v1/admin/online-payments', {
                withCredentials: true
            });

            if (data.success) {
                setPayments(data.onlineOrders);
                setTotalAmount(data.totalAmount);
            }
        } catch (error) {
            toast.error(error.response?.data?.message || 'Error fetching online payments');
        } finally {
            setLoading(false);
        }
    };

    const filteredPayments = payments.filter(payment => {
        if (!payment || !payment.user) return false;
        
        return (
            payment.orderId.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (payment.user.name && payment.user.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
            (payment.user.email && payment.user.email.toLowerCase().includes(searchTerm.toLowerCase())) ||
            payment.paymentInfo.id.toLowerCase().includes(searchTerm.toLowerCase())
        );
    });

    const PaymentCard = ({ payment }) => (
        <div className="bg-white rounded-lg shadow-sm p-4 mb-4">
            <div className="flex items-center justify-between">
                <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                        Order #{payment.orderId}
                    </h3>
                    <div className="flex items-center gap-2 text-sm text-gray-500 mt-1">
                        <User className="w-4 h-4" />
                        <span>{payment.user.name}</span>
                    </div>
                </div>
                <div className="text-[#dc2626] font-semibold">
                    ₹{payment.totalPrice}
                </div>
            </div>

            <div className="mt-4 grid grid-cols-2 gap-4">
                <div className="flex items-center gap-2 text-sm">
                    <CreditCard className="w-4 h-4 text-gray-400" />
                    <div className="text-gray-500 truncate">
                        ID: {payment.paymentInfo.id}
                    </div>
                </div>
                <div className="flex items-center gap-2 text-sm">
                    <Clock className="w-4 h-4 text-gray-400" />
                    <div className="text-gray-500">
                        {new Date(payment.createdAt).toLocaleDateString()}
                    </div>
                </div>
            </div>

            <div className="mt-4 flex justify-end">
                <button
                    onClick={() => navigate(`/admin/order/${payment._id}`)}
                    className="inline-flex items-center gap-2 text-[#dc2626] hover:bg-red-50 rounded-lg px-3 py-2 transition-colors"
                >
                    <Eye className="w-4 h-4" />
                    View Details
                </button>
            </div>
        </div>
    );

    if (loading) {
        return (
            <div className="flex justify-center items-center min-h-screen">
                <RefreshCw className="h-8 w-8 text-[#dc2626] animate-spin" />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 py-4 sm:py-8">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header with Back Button in top right */}
                <div className="flex justify-between items-center mb-6">
                    <h1 className="text-lg sm:text-xl font-bold text-[#dc2626] font-['Crimson_Text']">
                        Online Payments
                    </h1>
                    <button
                        onClick={() => navigate('/admin/dashboard')}
                        className="flex items-center gap-2 text-gray-600 hover:text-[#dc2626] transition-colors py-2 px-3 rounded-lg hover:bg-gray-100"
                    >
                        <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5" />
                        <span className="hidden sm:inline text-sm sm:text-base">Back to Dashboard</span>
                    </button>
                </div>
                
                <Card className="bg-white rounded-xl shadow-lg overflow-hidden">
                    <div className="p-4 sm:p-6 border-b border-gray-200">
                        <div className="flex flex-col space-y-4 sm:space-y-0 sm:flex-row sm:items-center sm:justify-between">
                            <div>
                                <div className="flex items-center flex-wrap gap-3">
                                    <span className="inline-flex items-center px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-medium bg-green-100 text-green-800">
                                        <DollarSign className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                                        ₹{totalAmount.toFixed(2)}
                                    </span>
                                    <p className="text-xs sm:text-sm text-gray-500">
                                        Total: {payments.length}
                                    </p>
                                </div>
                            </div>

                            <div className="relative w-full sm:w-64">
                                <input
                                    type="text"
                                    placeholder="Search payments..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-[#dc2626] focus:border-[#dc2626] transition-colors"
                                />
                                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                            </div>
                        </div>
                    </div>

                    {/* Desktop View */}
                    <div className="hidden md:block overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Order ID
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Customer
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Payment ID
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Amount
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Date
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Action
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {filteredPayments.map((payment) => (
                                    <tr key={payment._id} className="hover:bg-gray-50">
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="text-sm font-medium text-gray-900">
                                                {payment.orderId}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="flex items-center">
                                                <div className="flex-shrink-0 h-8 w-8 bg-gray-100 rounded-full flex items-center justify-center">
                                                    <User className="h-4 w-4 text-gray-500" />
                                                </div>
                                                <div className="ml-4">
                                                    <div className="text-sm font-medium text-gray-900">
                                                        {payment.user.name}
                                                    </div>
                                                    <div className="text-sm text-gray-500">
                                                        {payment.user.email}
                                                    </div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="text-sm text-gray-900">
                                                {payment.paymentInfo.id}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="text-sm font-medium text-[#dc2626]">
                                                ₹{payment.totalPrice}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="text-sm text-gray-900">
                                                {new Date(payment.createdAt).toLocaleDateString()}
                                            </div>
                                            <div className="text-sm text-gray-500">
                                                {new Date(payment.createdAt).toLocaleTimeString()}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <button
                                                onClick={() => navigate(`/admin/order/${payment._id}`)}
                                                className="p-2 text-[#dc2626] hover:bg-red-50 rounded-full transition-colors"
                                            >
                                                <Eye className="h-5 w-5" />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Mobile View - Enhanced */}
                    <div className="md:hidden p-4">
                        {/* Mobile Filters Button */}
                        <div className="flex justify-between items-center mb-4">
                            <p className="text-sm text-gray-500">
                                Showing {filteredPayments.length} of {payments.length}
                            </p>
                            <button 
                                onClick={() => setShowFilters(!showFilters)}
                                className="flex items-center gap-2 text-gray-600 px-3 py-1 border border-gray-300 rounded-lg"
                            >
                                <Filter className="w-4 h-4" />
                                <span>Filters</span>
                            </button>
                        </div>

                        {/* Mobile Filters Panel */}
                        {showFilters && (
                            <div className="bg-gray-50 p-4 mb-4 rounded-lg border border-gray-200">
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Sort by</label>
                                        <select className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                                            <option>Latest first</option>
                                            <option>Oldest first</option>
                                            <option>Amount: High to low</option>
                                            <option>Amount: Low to high</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Date range</label>
                                        <select className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                                            <option>Last 7 days</option>
                                            <option>Last 30 days</option>
                                            <option>Last 90 days</option>
                                            <option>All time</option>
                                        </select>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Payment Cards */}
                        <div className="space-y-4">
                            {filteredPayments.map((payment) => (
                                <PaymentCard key={payment._id} payment={payment} />
                            ))}
                        </div>

                        {/* Load more button for mobile */}
                        {filteredPayments.length > 10 && (
                            <button className="mt-4 w-full py-2 border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-50">
                                Load more
                            </button>
                        )}
                    </div>

                    {filteredPayments.length === 0 && (
                        <div className="text-center py-12">
                            <DollarSign className="mx-auto h-12 w-12 text-gray-400" />
                            <h3 className="mt-2 text-lg font-medium text-gray-900">No Online Payments</h3>
                            <p className="mt-1 text-sm text-gray-500">
                                {searchTerm ? 'No payments match your search criteria.' : 'No online payment transactions found.'}
                            </p>
                        </div>
                    )}
                </Card>
            </div>
        </div>
    );
};

export default OnlinePayments;